/**
 * All changes to this file must follow the Croissant design system.
 * @link https://www.figma.com/design/anJykRaoHi8nXjqpqlynmd/Croissant-%7C-Design-System?node-id=7684-30992
 */
import type { TypographyVariantsOptions } from '@mui/material/styles';

const FONT_WEIGHT_REGULAR = 400;
const FONT_WEIGHT_BOOK = 500;
const FONT_WEIGHT_DEMI = 600;
const FONT_WEIGHT_BOLD = 700;

/**
 * Croissant text styles, one variant per Figma style and named after it (`headline-reduced/large` is
 * `headlineReducedLarge`, `section/small` is `sectionSmall`). New UI uses these rather than the h1–h6
 * and body scale below.
 */
export const croissantTypography = {
    // Figma: headline/large
    headlineLarge: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '2.5rem', // 40px
        letterSpacing: '-0.025rem', // -0.4px
        lineHeight: '3rem', // 48px
        fontWeight: FONT_WEIGHT_DEMI,
    },
    // Figma: headline-reduced/large
    headlineReducedLarge: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '2rem', // 32px
        lineHeight: '2.25rem', // 36px
        fontWeight: FONT_WEIGHT_DEMI,
    },
    // Figma: headline/small
    headlineSmall: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '1.75rem', // 28px
        lineHeight: '2.25rem', // 36px
        fontWeight: FONT_WEIGHT_DEMI,
    },
    // Figma: section/large
    sectionLarge: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '1.625rem', // 26px
        letterSpacing: '-0.01em',
        lineHeight: '2rem', // 32px
        fontWeight: FONT_WEIGHT_DEMI,
    },
    // Figma: label/large
    labelLarge: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '1.3125rem', // 21px
        lineHeight: '1.75rem', // 28px
        fontWeight: FONT_WEIGHT_DEMI,
    },
    // Figma: label/small — step titles in the dashboard checklist.
    labelSmall: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '1.1875rem', // 19px
        lineHeight: '1.5rem', // 24px
        fontWeight: FONT_WEIGHT_DEMI,
    },
    // Figma: section/small
    sectionSmall: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '1.3125rem', // 21px
        lineHeight: '1.5rem', // 24px
        fontWeight: FONT_WEIGHT_DEMI,
    },
    // Figma: body/large
    bodyLarge: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '1rem', // 16px
        lineHeight: '1.5rem', // 24px
        fontWeight: FONT_WEIGHT_REGULAR,
    },
    // Figma: body-semi-bold/large
    bodySemiBold: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '1rem', // 16px
        lineHeight: '1.5rem', // 24px
        fontWeight: FONT_WEIGHT_DEMI,
    },
    // Figma: body-reduced/large
    bodyReducedLarge: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '0.875rem', // 14px
        lineHeight: '1.25rem', // 20px
        fontWeight: FONT_WEIGHT_REGULAR,
    },
    // Figma: body-reduced-semi-bold/large. Tabular numerals so figures in a column align on their digits.
    bodyReducedSemiBold: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '0.875rem', // 14px
        lineHeight: '1.25rem', // 20px
        fontWeight: FONT_WEIGHT_DEMI,
        fontFeatureSettings: '"lnum" 1, "tnum" 1',
    },
    // Figma: caption/large
    captionLarge: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '0.75rem', // 12px
        lineHeight: '1rem', // 16px
        fontWeight: FONT_WEIGHT_REGULAR,
    },
    // Figma: caption-semi-bold/large
    captionSemiBold: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '0.75rem', // 12px
        lineHeight: '1rem', // 16px
        fontWeight: FONT_WEIGHT_DEMI,
    },
    // Figma: overline/large
    overlineLarge: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '0.6875rem', // 11px
        letterSpacing: '0.08em',
        lineHeight: '0.75rem', // 12px
        fontWeight: FONT_WEIGHT_BOLD,
        textTransform: 'uppercase',
    },
} satisfies TypographyVariantsOptions;

const typography = {
    fontFamily: "'Mier B', sans-serif",
    fontSize: 16,
    fontWeightRegular: FONT_WEIGHT_REGULAR,
    fontWeightMedium: FONT_WEIGHT_BOOK,
    fontWeightBold: FONT_WEIGHT_DEMI,
    ...croissantTypography,
    h1: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '2rem', // 32px
        letterSpacing: '-0.01em',
        lineHeight: '2.25rem', // 36px
        fontWeight: FONT_WEIGHT_BOOK,
    },
    h2: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '1.5rem', // 24px
        letterSpacing: '-0.008em',
        lineHeight: '1.75rem', // 28px
        fontWeight: FONT_WEIGHT_BOOK,
    },
    h3: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '1.25rem', // 20px
        letterSpacing: '-0.006em',
        lineHeight: '1.5rem', // 24px
        fontWeight: FONT_WEIGHT_BOOK,
    },
    // h3 at bold, for a card title that has to outrank the body set beneath it.
    h3Bold: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '1.25rem', // 20px
        letterSpacing: '-0.006em',
        lineHeight: '1.5rem', // 24px
        fontWeight: FONT_WEIGHT_BOLD,
    },
    h4: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '1.125rem', // 18px
        letterSpacing: '-0.005em',
        lineHeight: '1.375rem', // 22px
        fontWeight: FONT_WEIGHT_BOOK,
    },
    h5: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '1rem', // 16px
        letterSpacing: '-0.004em',
        lineHeight: '1.25rem', // 20px
        fontWeight: FONT_WEIGHT_DEMI,
    },
    h6: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '0.875rem', // 14px
        letterSpacing: '-0.003em',
        lineHeight: '1.125rem', // 18px
        fontWeight: FONT_WEIGHT_DEMI,
    },
    subtitle1: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '1rem', // 16px
        letterSpacing: '-0.001em',
        lineHeight: '1.25rem', // 20px
        fontWeight: FONT_WEIGHT_BOOK,
    },
    subtitle2: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '0.875rem', // 14px
        letterSpacing: 0,
        lineHeight: '1.125rem', // 18px
        fontWeight: FONT_WEIGHT_BOOK,
    },
    body1: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '1rem', // 16px
        letterSpacing: '0.001em',
        lineHeight: '1.375rem', // 22px
        fontWeight: FONT_WEIGHT_REGULAR,
    },
    body1paragraph: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '1rem', // 16px
        letterSpacing: '0.001em',
        lineHeight: '1.5rem', // 24px
        fontWeight: FONT_WEIGHT_REGULAR,
    },
    body2: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '0.875rem', // 14px
        letterSpacing: '0.002em',
        lineHeight: '1.25rem', // 20px
        fontWeight: FONT_WEIGHT_REGULAR,
    },
    body2paragraph: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '0.875rem', // 14px
        letterSpacing: '0.002em',
        lineHeight: '1.375rem', // 22px
        fontWeight: FONT_WEIGHT_REGULAR,
    },
    button: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '0.875rem', // 14px
        letterSpacing: 0,
        lineHeight: '1rem', // 16px
        fontWeight: FONT_WEIGHT_DEMI,
        textTransform: 'initial',
    },
    overline: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '0.6875rem', // 11px
        letterSpacing: '0.04em',
        lineHeight: '0.75rem', // 12px
        fontWeight: FONT_WEIGHT_BOLD,
        textTransform: 'uppercase',
    },
    caption: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '0.75rem', // 12px
        letterSpacing: '0.001em',
        lineHeight: '1rem', // 16px
        textTransform: 'none',
        fontWeight: FONT_WEIGHT_BOOK,
    },
    body1Medium: {
        fontFamily: "'Mier B', sans-serif",
        fontSize: '1rem', // 16px
        letterSpacing: '0.001em',
        lineHeight: '1.375rem', // 22px
        fontWeight: FONT_WEIGHT_BOOK,
    },
} satisfies TypographyVariantsOptions;

export default typography;
